import React, { useContext, useState, useEffect } from 'react';
import { UserContext } from './UserContext';
import { API_ENDPOINTS } from '../config/api';
import { useToast } from '../hooks/useToast';
import Toast from './Toast';

const ExportAnalytics = () => {
    const context = useContext(UserContext);
    const { user, restaurant } = context || {};
    const restaurantId = restaurant?._id || user?.id;
    const { toasts, showToast, removeToast } = useToast();
    
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
    const [includeItems, setIncludeItems] = useState(true);
    const [includeSummary, setIncludeSummary] = useState(true);
    const [includeCustomers, setIncludeCustomers] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const [emailSchedule, setEmailSchedule] = useState({
        enabled: false,
        time: '09:00',
        email: restaurant?.email || '',
        additionalEmails: []
    });
    const [newEmailInput, setNewEmailInput] = useState('');
    
    // Load existing email schedule on mount
    useEffect(() => {
        const loadEmailSchedule = async () => {
            if (!restaurantId) return;
            
            try {
                const response = await fetch(`${API_ENDPOINTS.ANALYTICS_EXPORT.replace('/export', '')}/email-schedule/${restaurantId}`);
                const data = await response.json();
                
                if (data.schedule) {
                    setEmailSchedule({
                        enabled: data.schedule.enabled || false,
                        time: data.schedule.time || '09:00',
                        email: data.schedule.email || restaurant?.email || '',
                        additionalEmails: data.schedule.additionalEmails || []
                    });
                }
            } catch (error) {
                console.error('Failed to load email schedule:', error);
            }
        };
        
        loadEmailSchedule();
    }, [restaurantId, restaurant?.email]);
    
    const exportData = async (format) => {
        if (!restaurantId) {
            showToast('Restaurant ID not found. Please try again.', 'error');
            return;
        }

        if (!startDate || !endDate) {
            showToast('Please select both start and end dates.', 'error');
            return;
        }

        if (new Date(startDate) > new Date(endDate)) {
            showToast('Start date cannot be after end date.', 'error');
            return;
        }
        
        setIsExporting(true);
        try {
            const params = new URLSearchParams({
                restaurantId,
                startDate,
                endDate,
                format,
                includeItems: includeItems.toString(),
                includeSummary: includeSummary.toString(),
                includeCustomers: includeCustomers.toString()
            });

            const exportUrl = `${API_ENDPOINTS.ANALYTICS_EXPORT}?${params.toString()}`;
            
            // Use fetch to handle the download properly
            const response = await fetch(exportUrl);
            
            if (!response.ok) {
                throw new Error('Export failed');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `analytics_${startDate}_to_${endDate}.${format}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            
            showToast(`${format.toUpperCase()} exported successfully!`, 'success');
        } catch (error) {
            console.error('Export failed:', error);
            showToast('Export failed. Please try again.', 'error');
        } finally {
            setIsExporting(false);
        }
    };

    const handleScheduleEmail = async () => {
        if (!emailSchedule.email) {
            showToast('Please enter an email address.', 'error');
            return;
        }

        try {
            const response = await fetch(API_ENDPOINTS.ANALYTICS_SCHEDULE_EMAIL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    restaurantId,
                    email: emailSchedule.email,
                    additionalEmails: emailSchedule.additionalEmails,
                    time: emailSchedule.time,
                    enabled: emailSchedule.enabled
                })
            });

            if (!response.ok) throw new Error('Failed to schedule email');
            
            const totalEmails = 1 + (emailSchedule.additionalEmails?.length || 0);
            showToast(
                emailSchedule.enabled 
                    ? `Daily email reports scheduled for ${totalEmails} recipient(s)!` 
                    : 'Email schedule disabled.',
                'success'
            );
        } catch (error) {
            console.error('Schedule failed:', error);
            showToast('Failed to schedule email. Please try again.', 'error');
        }
    };

    const addEmailRecipient = () => {
        if (newEmailInput && newEmailInput.includes('@')) {
            setEmailSchedule({
                ...emailSchedule,
                additionalEmails: [...(emailSchedule.additionalEmails || []), newEmailInput]
            });
            setNewEmailInput('');
        } else if (newEmailInput) {
            showToast('Please enter a valid email address.', 'error');
        }
    };

    const removeEmailRecipient = (index) => {
        const updated = [...emailSchedule.additionalEmails];
        updated.splice(index, 1);
        setEmailSchedule({ ...emailSchedule, additionalEmails: updated });
    };

    const handleSendTestEmail = async () => {
        if (!emailSchedule.email) {
            showToast('Please enter an email address.', 'error');
            return;
        }

        setIsExporting(true);
        try {
            const allEmails = [emailSchedule.email, ...(emailSchedule.additionalEmails || [])];
            
            for (const email of allEmails) {
                const response = await fetch(`${API_ENDPOINTS.ANALYTICS_EXPORT.replace('/export', '/send-test-email')}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        restaurantId,
                        email,
                        startDate,
                        endDate
                    })
                });

                const data = await response.json();
                
                if (!response.ok) {
                    throw new Error(data.message || `Failed to send email to ${email}`);
                }
            }
            
            showToast(`email sent to ${allEmails.length} recipient(s)! Check your inbox.`, 'success');
        } catch (error) {
            console.error('Email failed:', error);
            showToast(error.message || 'Failed to send email. Check console for details.', 'error');
        } finally {
            setIsExporting(false);
        }
    };

    const setQuickDate = (days) => {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - days);
        
        // Format dates in local timezone to avoid UTC conversion issues
        const formatLocalDate = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };
        
        setStartDate(formatLocalDate(start));
        setEndDate(formatLocalDate(end));
    };

    return (
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-3 sm:p-4 space-y-3">
            <div className="flex items-center justify-between gap-2 mb-1">
                <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">Export & reporting</p>
                    <p className="text-[11px] text-stone-400 hidden sm:block">Download CSV/PDF and manage daily email reports.</p>
                </div>
            </div>
            
            {/* Date Range + Export options */}
            <div className="grid gap-3 lg:grid-cols-2">
                {/* Date Range Selection */}
                <div className="bg-stone-50/80 border border-stone-200 rounded-lg p-3 space-y-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">Date range</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div>
                            <label className="block text-[10px] font-medium text-stone-500 mb-0.5">Start date</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="w-full px-2.5 py-1.5 border border-stone-300 rounded-lg text-xs focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-medium text-stone-500 mb-0.5">End date</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="w-full px-2.5 py-1.5 border border-stone-300 rounded-lg text-xs focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none"
                            />
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 pt-1">
                        <button onClick={() => setQuickDate(0)} className="px-2.5 py-1 text-[11px] rounded-full border border-stone-300 text-stone-700 hover:bg-stone-50">Today</button>
                        <button onClick={() => setQuickDate(1)} className="px-2.5 py-1 text-[11px] rounded-full border border-stone-300 text-stone-700 hover:bg-stone-50">Yesterday</button>
                        <button onClick={() => setQuickDate(7)} className="px-2.5 py-1 text-[11px] rounded-full border border-stone-300 text-stone-700 hover:bg-stone-50">Last 7 days</button>
                        <button onClick={() => setQuickDate(30)} className="px-2.5 py-1 text-[11px] rounded-full border border-stone-300 text-stone-700 hover:bg-stone-50">Last 30 days</button>
                    </div>
                </div>

                {/* Export Options + Download */}
                <div className="bg-stone-50/80 border border-stone-200 rounded-lg p-3 space-y-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">Export options</p>
                    <div className="space-y-1.5">
                        <label className="flex items-center gap-2 cursor-pointer text-xs text-stone-700">
                            <input
                                type="checkbox"
                                checked={includeSummary}
                                onChange={(e) => setIncludeSummary(e.target.checked)}
                                className="w-3.5 h-3.5 text-orange-500 rounded border-stone-300 focus:ring-1 focus:ring-orange-400"
                            />
                            <span>Include summary statistics</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer text-xs text-stone-700">
                            <input
                                type="checkbox"
                                checked={includeItems}
                                onChange={(e) => setIncludeItems(e.target.checked)}
                                className="w-3.5 h-3.5 text-orange-500 rounded border-stone-300 focus:ring-1 focus:ring-orange-400"
                            />
                            <span>Include item details</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer text-xs text-stone-700">
                            <input
                                type="checkbox"
                                checked={includeCustomers}
                                onChange={(e) => setIncludeCustomers(e.target.checked)}
                                className="w-3.5 h-3.5 text-orange-500 rounded border-stone-300 focus:ring-1 focus:ring-orange-400"
                            />
                            <span>Include customer information</span>
                        </label>
                    </div>
                    <div className="flex flex-wrap gap-2 pt-1">
                        <button
                            onClick={() => exportData('csv')}
                            disabled={isExporting}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span>📄</span>
                            <span>{isExporting ? 'Exporting…' : 'Export CSV'}</span>
                        </button>
                        <button
                            onClick={() => exportData('pdf')}
                            disabled={isExporting}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span>📑</span>
                            <span>{isExporting ? 'Exporting…' : 'Export PDF'}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Email Scheduling */}
            <div className="border-t border-stone-200 pt-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">Daily email reports</p>
                    <label className="flex items-center gap-2 cursor-pointer text-xs text-stone-700">
                        <input
                            type="checkbox"
                            checked={emailSchedule.enabled}
                            onChange={(e) => setEmailSchedule({...emailSchedule, enabled: e.target.checked})}
                            className="w-3.5 h-3.5 text-orange-500 rounded border-stone-300 focus:ring-1 focus:ring-orange-400"
                        />
                        <span className="hidden sm:inline">Enable</span>
                    </label>
                </div>
                <div className="space-y-3">
                    <label className="hidden">
                        <input
                            type="checkbox"
                            checked={emailSchedule.enabled}
                            readOnly
                        />
                    </label>
                    
                    {emailSchedule.enabled && (
                        <>
                            <div>
                                <label className="block text-[10px] font-medium text-stone-500 mb-0.5">Primary email</label>
                                <input
                                    type="email"
                                    value={emailSchedule.email}
                                    onChange={(e) => setEmailSchedule({...emailSchedule, email: e.target.value})}
                                    placeholder="your@email.com"
                                    className="w-full px-2.5 py-1.5 border border-stone-300 rounded-lg text-xs focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none"
                                />
                            </div>
                            
                            {/* Additional Email Recipients */}
                            <div>
                                <label className="block text-[10px] font-medium text-stone-500 mb-1.5">Additional recipients</label>
                                
                                {/* Add New Email Input */}
                                <div className="flex gap-2 mb-2">
                                    <input
                                        type="email"
                                        value={newEmailInput}
                                        onChange={(e) => setNewEmailInput(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && addEmailRecipient()}
                                        placeholder="Enter email address"
                                        className="flex-1 px-2.5 py-1.5 border border-stone-300 rounded-lg text-xs focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none"
                                    />
                                    <button
                                        onClick={addEmailRecipient}
                                        type="button"
                                        className="px-3 py-1.5 text-xs bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-all duration-200"
                                    >
                                        Add
                                    </button>
                                </div>
                                
                                {/* List of Added Emails */}
                                {emailSchedule.additionalEmails && emailSchedule.additionalEmails.length > 0 && (
                                    <div className="space-y-2">
                                        {emailSchedule.additionalEmails.map((email, index) => (
                                            <div key={index} className="flex items-center gap-2 bg-stone-50 px-3 py-1.5 rounded-lg">
                                                <span className="text-xs text-stone-700 flex-1">{email}</span>
                                                <button
                                                    onClick={() => removeEmailRecipient(index)}
                                                    className="text-[11px] text-red-500 hover:text-red-700 font-medium"
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            
                            <div>
                                <label className="block text-[10px] font-medium text-stone-500 mb-0.5">Send time</label>
                                <input
                                    type="time"
                                    value={emailSchedule.time}
                                    onChange={(e) => setEmailSchedule({...emailSchedule, time: e.target.value})}
                                    className="w-full px-2.5 py-1.5 border border-stone-300 rounded-lg text-xs focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none"
                                />
                            </div>
                            <button
                                onClick={handleScheduleEmail}
                                className="w-full bg-stone-800 hover:bg-stone-900 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 shadow-sm hover:shadow-md mb-1.5"
                            >
                                Save Schedule
                            </button>
                            <button
                                onClick={handleSendTestEmail}
                                disabled={isExporting}
                                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isExporting ? 'Sending...' : '📧 Send Email Now'}
                            </button>
                        </>
                    )}
                </div>
            </div>
            
            {/* Toast Notifications */}
            {toasts.map(toast => (
                <Toast
                    key={toast.id}
                    message={toast.message}
                    type={toast.type}
                    duration={toast.duration}
                    onClose={() => removeToast(toast.id)}
                />
            ))}
        </div>
    );
};

export default ExportAnalytics;
